import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Tag,
  Button,
  Statistic,
  Row,
  Col,
  Spin,
  Empty,
  Divider,
  message,
  Table,
  Tabs,
  Progress
} from 'antd';
import {
  TrophyOutlined,
  VideoCameraOutlined,
  StarOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  HomeOutlined,
  TeamOutlined,
  RiseOutlined
} from '@ant-design/icons';
import { apiClient } from '../services/api';
import './DetailHistory.css';

const StudyProgramDetail = () => {
  const { unitCode } = useParams();
  const navigate = useNavigate();
  const [studyProgram, setStudyProgram] = useState(null);
  const [contributors, setContributors] = useState({
    dosen: [],
    mahasiswa: [],
    tendik: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStudyProgramDetail = async () => {
      setLoading(true);
      try {
        // Fetch study program details from backend
        const response = await apiClient.get(`/api/units/${unitCode}/detail`);

        if (response.data.status === 'success') {
          setStudyProgram(response.data.data.unit);
          setContributors({
            dosen: response.data.data.contributors.dosen || [],
            mahasiswa: response.data.data.contributors.mahasiswa || [],
            tendik: response.data.data.contributors.tendik || []
          });
        } else {
          message.error('Gagal memuat detail program studi');
        }
      } catch (error) {
        console.error('Error loading study program details:', error);
        message.error('Gagal memuat data dari server');
      } finally {
        setLoading(false);
      }
    };

    loadStudyProgramDetail();
  }, [unitCode]);

  const getRankColor = (rank) => {
    if (rank === 1) return 'gold';
    if (rank === 2) return 'silver';
    if (rank === 3) return 'bronze';
    return 'blue';
  };

  const getRankClass = (rank) => {
    if (rank === 1) return 'rank-badge-gold';
    if (rank === 2) return 'rank-badge-silver';
    if (rank === 3) return 'rank-badge-bronze';
    return 'rank-badge-blue';
  };

  const getRankIcon = (rank) => {
    if (rank <= 3) return <TrophyOutlined />;
    return <StarOutlined />;
  };

  if (loading) {
    return (
      <div className="history-loading">
        <Spin size="large" tip="Memuat detail program studi..." />
      </div>
    );
  }

  if (!studyProgram) {
    return (
      <div className="history-container">
        <div className="history-content">
          <Card className="not-found-card">
            <Empty
              description="Program studi tidak ditemukan"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
            <Button
              type="primary"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/')}
              className="back-button"
            >
              Kembali ke Leaderboard
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  const contributorColumns = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      key: 'rank',
      width: 70,
      render: (rank) => (
        <Tag color={getRankColor(rank)} className="rank-tag">
          #{rank}
        </Tag>
      ),
    },
    {
      title: 'Nama',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <div className="contributor-name-cell">{text}</div>
          <div className="contributor-id-cell">{record.external_id}</div>
        </div>
      ),
    },
    {
      title: 'Total Konten',
      dataIndex: 'total_verifications',
      key: 'total_verifications',
      width: 120,
      sorter: (a, b) => a.total_verifications - b.total_verifications,
    },
    {
      title: 'Total Poin',
      dataIndex: 'total_score',
      key: 'total_score',
      width: 120,
      sorter: (a, b) => a.total_score - b.total_score,
      render: (score) => <Tag color="blue">{score}</Tag>,
    },
  ];

  const tabItems = [
    {
      key: 'dosen',
      label: `Dosen (${contributors.dosen.length})`,
      children: (
        <Table
          columns={contributorColumns}
          dataSource={contributors.dosen}
          rowKey="external_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} dosen`,
          }}
        />
      ),
    },
    {
      key: 'mahasiswa',
      label: `Mahasiswa (${contributors.mahasiswa.length})`,
      children: (
        <Table
          columns={contributorColumns}
          dataSource={contributors.mahasiswa}
          rowKey="external_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} mahasiswa`,
          }}
        />
      ),
    },
    {
      key: 'tendik',
      label: `Tenaga Pendidik (${contributors.tendik.length})`,
      children: (
        <Table
          columns={contributorColumns}
          dataSource={contributors.tendik}
          rowKey="external_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} tenaga pendidik`,
          }}
        />
      ),
    },
  ];

  return (
    <div className="history-container">
      <div className="history-header">
        <Button
          icon={<HomeOutlined />}
          onClick={() => navigate('/')}
          className="back-button"
        >
          Kembali ke Leaderboard
        </Button>
      </div>

      <div className="history-content">
        {/* Study Program Profile Card */}
        <Card className="profile-card">
          <div className="profile-header">
            <div className="profile-info">
              <h1>{studyProgram.name}</h1>
              <p className="nim-nip">{studyProgram.code}</p>
              <div className="unit-tags-container">
                <Tag color="purple" className="unit-tag-type">
                  {studyProgram.type || 'Program Studi'}
                </Tag>
                {studyProgram.parent_unit_name && (
                  <>
                    <Tag color="blue" className="unit-tag-parent">
                      {studyProgram.parent_unit_name}
                    </Tag>
                    {studyProgram.parent_unit_code && (
                      <Tag color="geekblue" className="unit-tag-code">
                        {studyProgram.parent_unit_code}
                      </Tag>
                    )}
                  </>
                )}
              </div>
              {studyProgram.description && (
                <div className="unit-description-box">
                  <p className="unit-description-text">
                    {studyProgram.description}
                  </p>
                </div>
              )}
            </div>
            {studyProgram.rank && (
              <div className={`rank-badge ${getRankClass(studyProgram.rank)}`}>
                {getRankIcon(studyProgram.rank)}
                <span className="rank-text">Rank #{studyProgram.rank}</span>
              </div>
            )}
          </div>

          <Divider />

          <Row gutter={[24, 24]} className="stats-row">
            <Col xs={24} sm={6}>
              <div className="statistic-total-poin">
                <Statistic
                  title="Total Poin"
                  value={studyProgram.total_score || 0}
                  prefix={<StarOutlined />}
                />
              </div>
            </Col>
            <Col xs={24} sm={6}>
              <div className="statistic-total-konten">
                <Statistic
                  title="Total Konten"
                  value={studyProgram.total_verifications || 0}
                  prefix={<VideoCameraOutlined />}
                />
              </div>
            </Col>
            <Col xs={24} sm={6}>
              <div className="statistic-total-kontributor">
                <Statistic
                  title="Total Kontributor"
                  value={studyProgram.total_affiliates_with_content || 0}
                  prefix={<TeamOutlined />}
                  suffix={`/ ${studyProgram.total_members || 0}`}
                />
              </div>
            </Col>
            <Col xs={24} sm={6}>
              <div className="statistic-partisipasi">
                <Statistic
                  title="Tingkat Partisipasi"
                  value={studyProgram.participation_percentage || 0}
                  prefix={<RiseOutlined />}
                  suffix="%"
                />
              </div>
            </Col>
          </Row>

          <Divider />

          <Row gutter={[24, 24]}>
            <Col xs={24} sm={8}>
              <div className="contributor-circle-container">
                <div className="contributor-circle-label">Dosen Kontributor</div>
                <Progress
                  type="circle"
                  percent={studyProgram.total_dosen || 0}
                  format={(percent) => `${percent}%`}
                  strokeColor="#14B8A6"
                  width={80}
                />
                <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                  {studyProgram.dosen_count || 0} kontributor
                </div>
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div className="contributor-circle-container">
                <div className="contributor-circle-label">Mahasiswa Kontributor</div>
                <Progress
                  type="circle"
                  percent={studyProgram.total_mahasiswa || 0}
                  format={(percent) => `${percent}%`}
                  strokeColor="#14B8A6"
                  width={80}
                />
                <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                  {studyProgram.mahasiswa_count || 0} kontributor
                </div>
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div className="contributor-circle-container">
                <div className="contributor-circle-label">Tendik Kontributor</div>
                <Progress
                  type="circle"
                  percent={studyProgram.total_tendik || 0}
                  format={(percent) => `${percent}%`}
                  strokeColor="#722ed1"
                  width={80}
                />
                <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                  {studyProgram.tendik_count || 0} kontributor
                </div>
              </div>
            </Col>
          </Row>

          {(studyProgram.total_affiliates_with_content > 0) && (
            <>
              <Divider />
              <Row gutter={[24, 24]}>
                <Col xs={24} sm={12}>
                  <div className="statistic-avg-konten">
                    <Statistic
                      title="Rata-rata Konten per Kontributor"
                      value={studyProgram.total_affiliates_with_content > 0
                        ? (studyProgram.total_verifications / studyProgram.total_affiliates_with_content).toFixed(2)
                        : 0}
                      prefix={<VideoCameraOutlined />}
                    />
                  </div>
                </Col>
                <Col xs={24} sm={12}>
                  <div className="statistic-avg-poin">
                    <Statistic
                      title="Rata-rata Poin per Kontributor"
                      value={studyProgram.total_affiliates_with_content > 0
                        ? (studyProgram.total_score / studyProgram.total_affiliates_with_content).toFixed(2)
                        : 0}
                      prefix={<StarOutlined />}
                    />
                  </div>
                </Col>
              </Row>
            </>
          )}

          {/* Participation by Role */}
          <Divider />
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
              Tingkat Partisipasi per Role
            </h3>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <div style={{
                  padding: 16,
                  background: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
                  borderRadius: 8,
                  border: '1px solid #b7eb8f'
                }}>
                  <div style={{ fontSize: 14, color: '#14B8A6', fontWeight: 600, marginBottom: 8 }}>
                    👨‍🏫 Partisipasi Dosen
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#14B8A6', marginBottom: 4 }}>
                    {studyProgram.participation_dosen || 0}%
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {studyProgram.total_videos_dosen || 0} video dari {studyProgram.total_members_dosen || 0} dosen
                  </div>
                </div>
              </Col>
              <Col xs={24} md={8}>
                <div style={{
                  padding: 16,
                  background: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)',
                  borderRadius: 8,
                  border: '1px solid #91d5ff'
                }}>
                  <div style={{ fontSize: 14, color: '#14B8A6', fontWeight: 600, marginBottom: 8 }}>
                    👨‍🎓 Partisipasi Mahasiswa
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#0D9488', marginBottom: 4 }}>
                    {studyProgram.participation_mahasiswa || 0}%
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {studyProgram.total_videos_mahasiswa || 0} video dari {studyProgram.total_members_mahasiswa || 0} mahasiswa
                  </div>
                </div>
              </Col>
              <Col xs={24} md={8}>
                <div style={{
                  padding: 16,
                  background: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)',
                  borderRadius: 8,
                  border: '1px solid #d3adf7'
                }}>
                  <div style={{ fontSize: 14, color: '#722ed1', fontWeight: 600, marginBottom: 8 }}>
                    👷 Partisipasi Tendik
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#531dab', marginBottom: 4 }}>
                    {studyProgram.participation_tendik || 0}%
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {studyProgram.total_videos_tendik || 0} video dari {studyProgram.total_members_tendik || 0} tendik
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Card>

        {/* Contributors Card */}
        <Card
          className="history-card"
          title={
            <div className="card-title">
              <UserOutlined />
              <span>Kontributor</span>
            </div>
          }
        >
          <Tabs items={tabItems} defaultActiveKey="dosen" />
        </Card>
      </div>
    </div>
  );
};

export default StudyProgramDetail;
